import React, { FC, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import Button, { ButtonModeE } from "../../components/Button/Button";
import Form from "../../components/Form/Form";
import { ValidatorsE } from "../../components/Form/yupValidations";
import { H1, H3, P, Span } from "../../components/Text/Text";
import Icon from "../../components/Icon/Icon";
import Tooltip from "../../components/Tooltip/Tooltip";
import AsyncImage from "../../components/AsyncImage/AsyncImage";
import getImageLink from "../../utils/helpers/getImageLink";
import { IconColorE } from "../../models/enums";
import { FormFieldT } from "../../models/models";
import usePopup from "../../utils/hooks/usePopup";
import { useTranslate } from "../../utils/hooks/useTranslate";
import {
  ReDeliveryLookupResultT,
  useCreateReDeliveryOrderMutation,
  useLazyGetReusableDeliveryQuery,
} from "../../api/reDeliveryAPI";
import { useGetAllBrandsFullQuery } from "../../api/brandsAPI";
import { RoutesE } from "../../routes/routesData";
import { initialSelectedFileIds } from "./helpers/initialSelection";

import styles from "./reDelivery.module.scss";

export type ReDeliveryEntryT = "AgencyCampaign" | "DeliveryFirst" | "JumboDelivery";

type PropTypes = {
  entryType: ReDeliveryEntryT;
  // Entry a: attach to this already-existing campaign.
  existingCampaignId?: number;
  // Entries b/c: a source delivery number to pre-fill the lookup.
  prefilledNumber?: number;
  // spec-49 — the versions library starts the flow FROM a version: it arrives pre-checked on the
  // version-selection page (which restores a saved selection) and the user may add its siblings there.
  preselectedFileIds?: number[];
};

type StepT = "lookup" | "build";

const NAME_MAX_TOTAL = 60;

// Re-delivery CAMPAIGN-DEFINITION modal (Roadmap item 8). This modal only defines the new campaign:
// look up the past delivery, pick the target broadcasters (remove-only, from the source set), and — for
// entries that create a new campaign — the broadcast date + campaign name. On submit it creates the
// pending order (all versions pre-selected) and routes to the page-based flow: the version-selection
// page (/re-delivery/versions/:orderId) then the customer/billing page (/re-delivery/payment/:orderId),
// mirroring the delivery-service flow. No versions/customer/payment live in this modal anymore.
const ReDeliveryBuilder: FC<PropTypes> = ({ entryType, existingCampaignId, prefilledNumber, preselectedFileIds }) => {
  const { t } = useTranslate();
  const { removePopup } = usePopup();
  const navigate = useNavigate();
  // Brand options for the lookup, fetched directly (privacy: avoids pulling the full clients roster).
  const { data: brandsRaw = [], isFetching: isFetchingBrands } =
    useGetAllBrandsFullQuery({ asSelectOptions: false });
  const brandsSO = useMemo(
    () => (brandsRaw as any[]).map((b) => ({ value: +b.Id, label: b.Name })),
    [brandsRaw]
  );

  const isJumboDelivery = entryType === "JumboDelivery";
  const createsNewCampaign = entryType !== "AgencyCampaign";

  const [step, setStep] = useState<StepT>("lookup");
  const [lookupBrand, setLookupBrand] = useState<string>("");
  const [lookupBrandId, setLookupBrandId] = useState<number | undefined>(undefined);
  const [lookupError, setLookupError] = useState<ReDeliveryLookupResultT | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);

  const [sources, setSources] = useState<ReDeliveryLookupResultT[]>([]);
  // Opt-in: nothing is selected until the user picks. Still remove-only vs the source union server-side —
  // the posted set is always a subset of the union, so no broadcaster absent from the source can be added.
  const [selectedBroadcasterIds, setSelectedBroadcasterIds] = useState<number[]>([]);
  const [broadcasterError, setBroadcasterError] = useState<boolean>(false);
  const [dateError, setDateError] = useState<boolean>(false);
  const [customName, setCustomName] = useState<string>("");
  const [isNameEditMode, setIsNameEditMode] = useState<boolean>(true);
  const [broadcastingDate, setBroadcastingDate] = useState<string>("");
  const [appendNumber, setAppendNumber] = useState<string>("");

  const [triggerLookup, { isFetching: isLookingUp }] = useLazyGetReusableDeliveryQuery();
  const [createOrder, { isLoading: isCreatingOrder }] = useCreateReDeliveryOrderMutation();

  // ---- Aggregates across all looked-up sources --------------------------------------------------
  const source = sources[0]?.Source || null;

  const allVersions = useMemo(() => sources.flatMap((s) => s.Versions || []), [sources]);

  const unionBroadcasters = useMemo(() => {
    const map = new Map<number, string>();
    sources.forEach((s) =>
      (s.UnionBroadcasters || []).forEach((b) => {
        if (!map.has(b.BroadcasterId)) map.set(b.BroadcasterId, b.Name);
      })
    );
    return Array.from(map.entries()).map(([BroadcasterId, Name]) => ({ BroadcasterId, Name }));
  }, [sources]);

  const targetBroadcasterIds = useMemo(
    () => unionBroadcasters.map((b) => b.BroadcasterId).filter((id) => selectedBroadcasterIds.includes(id)),
    [unionBroadcasters, selectedBroadcasterIds]
  );

  // The broadcasters of the new campaign are chosen deliberately — nothing is pre-selected, EXCEPT when
  // the source union holds a single broadcaster (there is no choice to make). Also drops ids that left
  // the union (only reachable if the dormant multi-source append is re-enabled).
  useEffect(() => {
    if (unionBroadcasters.length === 1) {
      setSelectedBroadcasterIds([unionBroadcasters[0].BroadcasterId]);
      return;
    }
    setSelectedBroadcasterIds((prev) => {
      const kept = prev.filter((id) => unionBroadcasters.some((b) => b.BroadcasterId === id));
      return kept.length === prev.length ? prev : kept;
    });
  }, [unionBroadcasters]);

  const sourceNumbers = useMemo(
    () => sources.map((s) => s.Source?.Number).filter(Boolean) as number[],
    [sources]
  );

  // ---- Campaign name (auto prefix + editable custom) --------------------------------------------
  const namePrefix = useMemo(() => {
    const datePart =
      broadcastingDate && dayjs(broadcastingDate).isValid()
        ? dayjs(broadcastingDate).format("YYYY-MM")
        : `[${t`Broadcasting date`}]`;
    const adv = source?.AdvertiserName || `[${t`Advertiser Name`}]`;
    const brand = source?.BrandName || `[${t`Brand Name`}]`;
    return adv === brand ? `${datePart} - ${brand}` : `${datePart} - ${adv} - ${brand}`;
  }, [broadcastingDate, source, t]);

  const nameMaxChars = Math.max(10, NAME_MAX_TOTAL - namePrefix.length - 3);
  const fullCampaignName = customName ? `${namePrefix} — ${customName}` : namePrefix;

  // ---- Lookup -----------------------------------------------------------------------------------
  const runLookup = async (
    number: number,
    brandName: string | undefined,
    brandId: number | undefined,
    appendToNumber?: number
  ) => {
    setLookupError(null);
    setRequestError(null);
    try {
      const res = await triggerLookup({ number, brandName, brandId, appendToNumber }).unwrap();
      if (!res.IsEligible) {
        setLookupError(res);
        return;
      }
      setSources((prev) => [...prev, res]);
      setStep("build");
    } catch (e) {
      setRequestError("Something went wrong while looking up the delivery. Please try again.");
    }
  };

  const ineligibilityMessage = (res: ReDeliveryLookupResultT): string => {
    switch (res.ReasonCode) {
      case "NotFound":
        return "No delivery was found with this number.";
      case "BrandMismatch":
        return "The delivery number and brand name do not match.";
      case "NotOwned":
        return isJumboDelivery
          ? "This delivery was created under a different company account and cannot be reused."
          : "This delivery belongs to a different advertising agency and cannot be reused.";
      case "BadStatus":
        return "Only deliveries that have already been sent to broadcasters (status Sent and pending download, Downloaded by broadcaster, or Closed) can be reused.";
      case "TooFresh":
        return "This delivery is too recent to be reused. Only deliveries that were broadcast long enough ago can be reused.";
      case "NoEligibleVersions":
        return "This delivery has no versions that can be reused. A version must be fully encoded to be reused.";
      case "AppendMismatch":
        return "The added delivery must share the same advertiser, brand, and advertising agency as the first delivery.";
      default:
        return "This delivery cannot be reused for a new campaign.";
    }
  };

  const handleLookupSubmit = (data: any) => {
    const n = prefilledNumber || parseInt(data?.deliveryNumber, 10);
    if (!n) return;
    const brandName = isJumboDelivery ? data?.brand?.label || undefined : undefined;
    const brandId = isJumboDelivery ? data?.brand?.value || undefined : undefined;
    setLookupBrand(brandName || "");
    setLookupBrandId(brandId);
    runLookup(n, brandName, brandId);
  };

  const lookupFields: FormFieldT[] = [
    {
      label: "Delivery number",
      type: "number",
      validation: ValidatorsE.REQUIRED_NUMBER,
      defaultValue: prefilledNumber ? String(prefilledNumber) : undefined,
      props: prefilledNumber ? { readOnly: true } : undefined,
      hintText: "Enter the number of the past delivery you want to reuse.",
    },
    ...(isJumboDelivery
      ? [
          {
            label: "Brand",
            selectOptions: brandsSO,
            validation: ValidatorsE.SELECT_REQUIRED,
          } as FormFieldT,
        ]
      : []),
  ];

  const handleAppend = () => {
    const n = parseInt(appendNumber, 10);
    if (!n || !sources[0]?.Source) return;
    setAppendNumber("");
    runLookup(
      n,
      isJumboDelivery ? lookupBrand : undefined,
      isJumboDelivery ? lookupBrandId : undefined,
      sources[0].Source.Number
    );
  };

  // Free toggling within the source union — the "at least one" rule is a validation on Continue, not a
  // guard here, so the user may clear the set and be told what is missing.
  const toggleBroadcaster = (id: number) => {
    setBroadcasterError(false);
    setSelectedBroadcasterIds((prev) =>
      prev.includes(id) ? prev.filter((b) => b !== id) : [...prev, id]
    );
  };

  // ---- Create order + route to the page-based flow ----------------------------------------------
  const handleCreateOrder = async () => {
    setRequestError(null);

    // Both selections are deliberate: tell the user what is missing instead of silently disabling.
    const missingBroadcasters = targetBroadcasterIds.length === 0;
    const missingDate = createsNewCampaign && !broadcastingDate;
    setBroadcasterError(missingBroadcasters);
    setDateError(missingDate);
    if (missingBroadcasters || missingDate) return;

    const body = {
      // Customer details are collected later on the customer/billing page.
      CompanyName: "",
      ContactName: "",
      EmailAddress: "",
      PhoneNumber: "",
      InvoiceName: "",
      VATNumber: "",
      EntryType: entryType,
      ExistingCampaignId: existingCampaignId ?? null,
      NewCampaignName: createsNewCampaign ? fullCampaignName || null : null,
      BroadcastingDate: createsNewCampaign && broadcastingDate ? broadcastingDate : null,
      SourceProjectNumbers: sourceNumbers,
      // Versions are chosen on the version-selection page, not here — so the order starts with none,
      // except: a version the caller pre-selected (spec 49, the versions library) and the single-version
      // case (no choice to make). See helpers/initialSelection.ts.
      SelectedProjectFileIds: initialSelectedFileIds(preselectedFileIds, allVersions),
      TargetBroadcasterIds: targetBroadcasterIds,
    };

    try {
      const order = await createOrder(body).unwrap();
      removePopup();
      navigate(RoutesE.RE_DELIVERY_VERSIONS.replace(":orderId", String(order.Id)));
    } catch (e) {
      setRequestError(
        "Something went wrong while creating the campaign. Please check the details and try again."
      );
    }
  };

  // ---- Render -----------------------------------------------------------------------------------
  const title = source ? `Reuse a previous delivery - ${source.Number}` : "Reuse a previous delivery";

  return (
    <div className={styles.builder}>
      <H1 className={styles.title}>{title}</H1>

      {step === "lookup" && (
        <div className={styles.lookupStep}>
          <P className={styles.intro}>
            Reuse a delivery lets you create a new campaign from an ad that has already been sent to the
            broadcasters — without uploading or encoding the media again. The broadcasters already hold the
            original files, so you simply pick the past delivery, choose the versions and broadcasters, and
            we build the new campaign and resend it to them for you.
          </P>
          <P className={styles.description}>
            {isJumboDelivery
              ? "Enter the delivery number and select the brand of the past delivery you want to reuse in a new campaign."
              : "Enter the delivery number of the past delivery you want to reuse."}
          </P>

          <Form fields={lookupFields} formID="re-delivery-lookup" onSubmit={handleLookupSubmit} columns={2} />
          {isJumboDelivery && !brandsSO?.length && isFetchingBrands && (
            <Span className={styles.fieldHint}>Loading brands…</Span>
          )}

          {lookupError && (
            <div className={styles.ineligible}>
              <P className={styles.ineligibleTitle}>This delivery cannot be reused for a new campaign.</P>
              <P>{ineligibilityMessage(lookupError)}</P>
            </div>
          )}
          {requestError && (
            <div className={styles.ineligible}>
              <P>{requestError}</P>
            </div>
          )}

          <div className={styles.footerButtons}>
            <Button mode={ButtonModeE.OUTLINED} onClick={removePopup}>
              Cancel
            </Button>
            <Button form="re-delivery-lookup" disabled={isLookingUp}>
              Continue
            </Button>
          </div>
        </div>
      )}

      {step === "build" && (
        <div className={styles.buildStep}>
          <section className={styles.section}>
            <H3 className={styles.sectionTitle}>Target broadcasters</H3>
            <Span className={styles.sectionHint}>
              Select the broadcasters this new campaign will be sent to. You can only choose from the
              broadcasters of the original delivery.
            </Span>
            <ul className={styles.broadcasterGrid}>
              {unionBroadcasters.map((b) => {
                const included = selectedBroadcasterIds.includes(b.BroadcasterId);
                return (
                  <li
                    key={b.BroadcasterId}
                    className={included ? styles.broadcasterTileSelected : styles.broadcasterTile}
                    onClick={() => toggleBroadcaster(b.BroadcasterId)}
                  >
                    <Tooltip text={b.Name} delayShow={100}>
                      <AsyncImage link={getImageLink({ id: b.BroadcasterId })} />
                    </Tooltip>
                    <span className={styles.broadcasterName}>{b.Name}</span>
                  </li>
                );
              })}
            </ul>

            {broadcasterError && (
              <P className={styles.validationError}>
                Please select at least one broadcaster to send the new campaign to.
              </P>
            )}

            {/* "Add another delivery" (append a second source delivery) is intentionally hidden for now —
                it adds operational complexity (multi-source grouping/pricing). The append handler/state
                (handleAppend / appendNumber) are kept in place so it can be re-enabled later. */}
          </section>

          {createsNewCampaign && (
            <section className={styles.section}>
              <H3 className={styles.sectionTitle}>Campaign details</H3>
              <div className={styles.campaignGrid}>
                <div className={styles.readonlyField}>
                  <Span className={styles.fieldLabelText}>Advertiser</Span>
                  <div className={styles.readonlyValue}>{source?.AdvertiserName || "—"}</div>
                </div>
                <div className={styles.readonlyField}>
                  <Span className={styles.fieldLabelText}>Brand</Span>
                  <div className={styles.readonlyValue}>{source?.BrandName || "—"}</div>
                </div>

                <Form
                  fields={[
                    {
                      label: "Broadcasting date",
                      name: "BroadcastingDate",
                      type: "date",
                      hintText: "Select the intended broadcast date for the new campaign.",
                      defaultValue: broadcastingDate || undefined,
                      onChange: (value: string) => {
                        setDateError(false);
                        setBroadcastingDate(value);
                      },
                      props: { min: new Date().toISOString().split("T")[0] },
                    },
                  ]}
                  formID="re-delivery-campaign-date"
                  onSubmit={() => {}}
                  withSubmitButton={false}
                  columns={1}
                />

                <div className={styles.nameField}>
                  <Span className={styles.fieldLabelText}>Campaign name</Span>
                  {isNameEditMode ? (
                    <div className={styles.nameEditRow}>
                      <input
                        type="text"
                        value={customName}
                        maxLength={nameMaxChars}
                        placeholder={t`[Custom name]`}
                        onChange={(e) => setCustomName(e.target.value)}
                      />
                      <Icon
                        name="approved"
                        color={IconColorE.NONE}
                        className={styles.nameToggleIcon}
                        onClick={() => setIsNameEditMode(false)}
                      />
                      {customName && (
                        <span className={styles.nameCounter}>
                          {customName.length}/{nameMaxChars}
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className={styles.nameDisplayRow} onClick={() => setIsNameEditMode(true)}>
                      <span className={styles.namePrefix}>{namePrefix} — </span>
                      <span className={styles.customNamePart}>{customName || t`[Custom name]`}</span>
                      <Icon
                        name="edit"
                        color={IconColorE.GRAY}
                        className={styles.nameToggleIcon}
                        onClick={() => setIsNameEditMode(true)}
                      />
                    </div>
                  )}
                  <p className={styles.namePreview}>{fullCampaignName}</p>
                </div>
              </div>

              {dateError && (
                <P className={styles.validationError}>
                  Please select the broadcasting date for the new campaign.
                </P>
              )}
            </section>
          )}

          {requestError && (
            <div className={styles.ineligible}>
              <P>{requestError}</P>
            </div>
          )}

          <div className={styles.footerButtons}>
            <Button mode={ButtonModeE.OUTLINED} onClick={() => setStep("lookup")}>
              Back
            </Button>
            <Button onClick={handleCreateOrder} useGlobalLoading disabled={isCreatingOrder}>
              Continue
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReDeliveryBuilder;
